import i18next from 'i18next'
import { rootWidget } from './site/root/rootWidget'
import { createRoot } from 'react-dom/client'

// STYLING IMPORTS
import './styling/custom-theme/stylesheets/spa.scss' // PROJECT
import './site/root/globals'
import { RootState } from './site/root/rootState'

export type RootProps = {
  redirects: SPARedirect[]
  useSSR: boolean
}

export type SPARedirect = {
  OriginalSlug: string
  NewSlug: string
  Permanent: boolean
}

const maybeRemoveSSRRoot = () => (state: RootState) => {
  const root = document.getElementById('root')
  const ssrRoot = document.getElementById('rootSSR')

  if (!window.useSSR) {
    ssrRoot?.parentNode?.removeChild(ssrRoot)
    return
  }

  let releaseCounter = 20

  const hasAsyncLoading = (obj: RootState | unknown, parent: RootState | unknown) => {
    if (obj == undefined) return false
    const entries = Object.entries(obj)

    const traverseKeys = (requiredKeys?: Array<string>) => {
      for (let [key, value] of entries) {
        if (value == null || value == undefined) continue
        if (typeof value === 'function') continue
        if (value instanceof Date) continue

        if ((value as any)['type'] == 'async-widget') {
          if ((value as any)['kind'] === 'loading' && requiredKeys?.includes(key)) {
            console.warn('found async loading', value, obj, parent)
            return true
          }
        }

        if (typeof value === 'string' || value instanceof String) continue
        let hasValueAsyncLoading = hasAsyncLoading(value, obj)
        if (hasValueAsyncLoading) return true
      }
    }

    if (typeof obj === 'object' && 'page' in obj) {
      const state = obj as RootState

      traverseKeys()
      return
    }

    if ((obj as any)['kind'] == 'fromPromise') return false

    traverseKeys()

    return false
  }

  let firstTime = true

  let removeSSR = () => {
    setTimeout(
      () => {
        firstTime = false
        if (hasAsyncLoading(state, undefined) && releaseCounter > 0) {
          releaseCounter--
          // console.log("found loading in state", state, releaseCounter)
          removeSSR()
          return
        }

        if (root) {
          root.style.display = ''
        }
        if (ssrRoot) {
          ssrRoot.style.display = 'none'
          setTimeout(() => ssrRoot.parentNode?.removeChild(ssrRoot), 100)
        }
      },
      firstTime ? 1000 : 200
    )
  }

  removeSSR()
}

export const main = async (props: RootProps) => {
  const headers = new Headers()
  headers.append('content-type', 'application/json')

  const res = await fetch(`/api/v1/sharepoint/translations.json`, {
    method: 'get',
    credentials: 'include',
    headers: headers,
  })

  const resources = await res.json()

  const url = window.location.pathname

  await i18next.init({
    ns: ['error'],
    lng: url === 'en' || url.startsWith('/en') ? 'en' : 'nl',
    resources: resources,
    interpolation: {
      format: function(value: string, format, lng) {
        if (format === 'strong') return `<strong>${value} kg</strong>`
        return value
      }
    }
  })

  const container = document.querySelector('#root')
  if (container !== null) {
    const root = createRoot(container)
    root.render(
      rootWidget(props.redirects).run(
        (state) => {
          maybeRemoveSSRRoot()(state)
        }
      )
    )
  }
}

main({ redirects: window.redirects, useSSR: window.useSSR })
